import { useState } from 'react';
import { createWorker } from 'tesseract.js';
import { ArrowDownAZ, ChevronDown, ChevronRight, ShoppingCart } from 'lucide-react';
import { useData, useApp } from '../context.jsx';
import { getTodayKey } from '../lib/helpers.js';
import { UNASSIGNED_STORE } from '../lib/constants.js';
import { Composer } from '../components/ui.jsx';
import { Inventory } from '../components/Inventory.jsx';
import { StorePreview } from '../components/StorePreview.jsx';
import { ReceiptScanner, GroceryStoreColumn, parseReceiptItems, getBestReceiptMatch } from '../components/ReceiptScanner.jsx';

export default function Shopping() {
  const { data, updateData } = useData();
  const { drafts, setDrafts, addItem, removeItem } = useApp();

  const [collapsed, setCollapsed] = useState({ pantry: false, supplies: false, groceries: false });
  const [receiptItems, setReceiptItems] = useState([]);
  const [receiptStatus, setReceiptStatus] = useState('');

  const stores = [...data.stores].sort((a, b) => a.localeCompare(b));
  const receiptStores = stores.length ? stores : [UNASSIGNED_STORE];
  const selectedReceiptStore = drafts.receiptStore || receiptStores[0];

  const toggleSection = (section) => setCollapsed((current) => ({ ...current, [section]: !current[section] }));

  const updateQuantity = (collection, id, quantity) => {
    updateData((current) => ({
      ...current,
      [collection]: current[collection].map((item) => item.id === id ? { ...item, quantity } : item)
    }));
  };

  const toggleLow = (collection, id) => {
    updateData((current) => ({
      ...current,
      [collection]: current[collection].map((item) => item.id === id ? { ...item, low: !item.low } : item)
    }));
  };

  const updateGroceryPrice = (id, price) => {
    updateData((current) => ({
      ...current,
      groceries: current.groceries.map((item) => item.id === id ? { ...item, price } : item)
    }));
  };

  const moveGroceryToStore = (id, store) => {
    updateData((current) => ({
      ...current,
      groceries: current.groceries.map((item) => item.id === id ? { ...item, store } : item)
    }));
  };

  const addStore = () => {
    const name = drafts.store.trim();
    if (!name || name === UNASSIGNED_STORE) return;
    updateData((current) => (
      current.stores.some((s) => s.toLowerCase() === name.toLowerCase())
        ? current
        : { ...current, stores: [...current.stores, name].sort((a, b) => a.localeCompare(b)) }
    ));
    setDrafts((current) => ({ ...current, store: '' }));
  };

  const renameStore = (from, to) => {
    const name = to.trim();
    if (!name || name === UNASSIGNED_STORE) return;
    updateData((current) => ({
      ...current,
      stores: current.stores.map((s) => s === from ? name : s).sort((a, b) => a.localeCompare(b)),
      groceries: current.groceries.map((item) => item.store === from ? { ...item, store: name } : item)
    }));
  };

  const removeStore = (storeName) => {
    updateData((current) => ({
      ...current,
      stores: current.stores.filter((s) => s !== storeName),
      groceries: current.groceries.map((item) => item.store === storeName ? { ...item, store: UNASSIGNED_STORE } : item)
    }));
  };

  const sortGroceries = () => {
    updateData((current) => ({
      ...current,
      groceries: [...current.groceries].sort((a, b) => a.name.localeCompare(b.name))
    }));
  };

  const completeTrip = (storeName) => {
    const boughtItems = data.groceries.filter((item) => item.store === storeName && item.bought);
    if (!boughtItems.length) return;
    updateData((current) => ({
      ...current,
      groceries: current.groceries.map((item) => (
        item.store === storeName && item.bought ? { ...item, bought: false } : item
      )),
      receiptTrips: [
        {
          id: crypto.randomUUID(),
          date: getTodayKey(),
          store: storeName,
          items: boughtItems.map((item) => ({ name: item.name, price: item.price, groceryId: item.id }))
        },
        ...current.receiptTrips
      ].slice(0, 20)
    }));
  };

  const parseReceiptText = (text) => {
    const parsed = parseReceiptItems(text).map((item) => {
      const match = getBestReceiptMatch(item.name, data.groceries);
      return { ...item, matchId: match.confidence >= 0.34 ? match.id : '', confidence: match.confidence };
    });
    setReceiptItems(parsed);
    setReceiptStatus(parsed.length ? `${parsed.length} receipt lines found` : 'No line items with prices found');
  };

  const scanReceiptImage = async (file) => {
    if (!file) return;
    setReceiptStatus('Scanning receipt...');
    try {
      const worker = await createWorker('eng');
      const result = await worker.recognize(file);
      await worker.terminate();
      const text = result.data.text || '';
      setDrafts((current) => ({ ...current, receiptText: text }));
      parseReceiptText(text);
    } catch {
      setReceiptStatus('Could not scan image. Paste receipt text instead.');
    }
  };

  const updateReceiptMatch = (id, matchId) => {
    setReceiptItems((current) => current.map((item) => item.id === id ? { ...item, matchId } : item));
  };

  const applyReceiptMatches = () => {
    const matches = receiptItems.filter((item) => item.matchId);
    if (!matches.length) { setReceiptStatus('Choose at least one item match first'); return; }
    updateData((current) => ({
      ...current,
      groceries: current.groceries.map((grocery) => {
        const match = matches.find((item) => item.matchId === grocery.id);
        return match ? { ...grocery, price: match.price, store: selectedReceiptStore, bought: false } : grocery;
      }),
      receiptTrips: [
        {
          id: crypto.randomUUID(),
          date: getTodayKey(),
          store: selectedReceiptStore,
          items: matches.map((item) => ({ name: item.name, price: item.price, groceryId: item.matchId }))
        },
        ...current.receiptTrips
      ].slice(0, 20)
    }));
    setReceiptStatus(`${matches.length} prices applied to ${selectedReceiptStore}`);
  };

  return (
    <section className="view-grid">
      <Inventory
        title="Pantry"
        items={data.pantry}
        draft={drafts.pantry}
        collapsed={collapsed.pantry}
        onToggle={() => toggleSection('pantry')}
        onDraft={(value) => setDrafts((current) => ({ ...current, pantry: value }))}
        onAdd={() => addItem('pantry', 'pantry', (name) => ({ id: crypto.randomUUID(), name, quantity: 'New', low: false }))}
        onQuantityChange={(id, quantity) => updateQuantity('pantry', id, quantity)}
        onToggleLow={(id) => toggleLow('pantry', id)}
        onRemove={(id) => removeItem('pantry', id)}
      />
      <Inventory
        title="Household Supplies"
        items={data.supplies}
        draft={drafts.supply}
        collapsed={collapsed.supplies}
        onToggle={() => toggleSection('supplies')}
        onDraft={(value) => setDrafts((current) => ({ ...current, supply: value }))}
        onAdd={() => addItem('supplies', 'supply', (name) => ({ id: crypto.randomUUID(), name, quantity: 'New', low: false }))}
        onQuantityChange={(id, quantity) => updateQuantity('supplies', id, quantity)}
        onToggleLow={(id) => toggleLow('supplies', id)}
        onRemove={(id) => removeItem('supplies', id)}
      />
      <Composer
        icon={ShoppingCart}
        value={drafts.grocery}
        placeholder="Add grocery item..."
        onChange={(value) => setDrafts((current) => ({ ...current, grocery: value }))}
        onAdd={() => addItem('groceries', 'grocery', (name) => ({ id: crypto.randomUUID(), name, price: '', store: UNASSIGNED_STORE, bought: false }))}
      />
      <ReceiptScanner
        stores={receiptStores}
        selectedStore={selectedReceiptStore}
        receiptText={drafts.receiptText}
        receiptItems={receiptItems}
        groceries={data.groceries}
        status={receiptStatus}
        trips={data.receiptTrips}
        onStoreChange={(store) => setDrafts((current) => ({ ...current, receiptStore: store }))}
        onTextChange={(text) => setDrafts((current) => ({ ...current, receiptText: text }))}
        onParse={() => parseReceiptText(drafts.receiptText)}
        onImage={scanReceiptImage}
        onMatchChange={updateReceiptMatch}
        onApply={applyReceiptMatches}
      />
      <section className={collapsed.groceries ? 'panel wide collapsible-panel is-collapsed' : 'panel wide collapsible-panel'}>
        <button className="section-heading collapse-heading" onClick={() => toggleSection('groceries')} type="button" aria-expanded={!collapsed.groceries}>
          <h3>Grocery list by store</h3>
          {collapsed.groceries ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
        </button>
        {!collapsed.groceries && (
          <>
            <div className="store-toolbar">
              <div className="mini-composer">
                <input
                  value={drafts.store}
                  onChange={(event) => setDrafts((current) => ({ ...current, store: event.target.value }))}
                  onKeyDown={(event) => event.key === 'Enter' && addStore()}
                  placeholder="Add store..."
                />
                <button className="icon-button add" onClick={addStore} type="button" aria-label="Add store">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                </button>
              </div>
              <button className="text-button" onClick={sortGroceries} type="button"><ArrowDownAZ size={15} /> Sort A-Z</button>
            </div>
            <div className="store-board">
              <GroceryStoreColumn
                title={UNASSIGNED_STORE}
                items={data.groceries.filter((item) => item.store === UNASSIGNED_STORE || !item.store)}
                onDropItem={(id) => moveGroceryToStore(id, UNASSIGNED_STORE)}
                onToggleBought={(id) => updateData((current) => ({ ...current, groceries: current.groceries.map((g) => g.id === id ? { ...g, bought: !g.bought } : g) }))}
                onPriceChange={updateGroceryPrice}
                onRemove={(id) => removeItem('groceries', id)}
              />
              {stores.map((store) => (
                <GroceryStoreColumn
                  key={store}
                  title={store}
                  editable
                  items={data.groceries.filter((item) => item.store === store)}
                  onRename={(name) => renameStore(store, name)}
                  onRemoveStore={() => removeStore(store)}
                  onDropItem={(id) => moveGroceryToStore(id, store)}
                  onToggleBought={(id) => updateData((current) => ({ ...current, groceries: current.groceries.map((g) => g.id === id ? { ...g, bought: !g.bought } : g) }))}
                  onPriceChange={updateGroceryPrice}
                  onRemove={(id) => removeItem('groceries', id)}
                  onCompleteTrip={() => completeTrip(store)}
                />
              ))}
            </div>
          </>
        )}
      </section>
    </section>
  );
}
